/**
 * Context injection utilities for LLM data reference.
 *
 * Enables LLM to reference specific records by injecting filtered data
 * into context with natural language summaries.
 */
import { encode } from '@toon-format/toon';

import { roundRowForDisplay } from './round-for-display.ts';
import { reduceToTokenLimit } from './token-estimation.ts';

const SAMPLE_WARNING =
  'SAMPLE — DISPLAY ONLY. Do NOT derive counts, distinct values, distributions, or "top N" answers from these rows. They are a subset of the full dataset. For any quantitative answer, use aggregations or groupBy on the full dataset.';

/**
 * Detect low-cardinality fields suitable for enumeration.
 * A field is low-cardinality if distinct/count ratio < 0.1 and distinct <= maxDistinct.
 */
export function detectEnumFields(data: Record<string, unknown>[], maxDistinct = 10): Record<string, string[]> {
  if (data.length === 0) {
    return {};
  }

  const firstRecord = data[0];
  if (!firstRecord) {
    return {};
  }

  const result: Record<string, string[]> = {};
  const columns = Object.keys(firstRecord);

  for (const col of columns) {
    const values = data.map((row) => row[col]).filter((v) => v != null);
    const distinct = new Set(values.map(String));
    const ratio = distinct.size / data.length;

    if (ratio < 0.1 && distinct.size <= maxDistinct && distinct.size > 0) {
      result[col] = [...distinct].sort();
    }
  }

  return result;
}

/**
 * Generate natural language summary of dataset characteristics.
 */
export function summarizeDataset(data: Record<string, unknown>[]): string {
  if (data.length === 0) {
    return 'Empty dataset';
  }

  const firstRecord = data[0];
  if (!firstRecord) {
    return 'Empty dataset';
  }

  const columns = Object.keys(firstRecord);
  const enumFields = detectEnumFields(data);

  const parts = [`${data.length} records with columns: ${columns.join(', ')}`];

  for (const [field, values] of Object.entries(enumFields)) {
    parts.push(`${field} values: ${encode(values).replace(/\n/g, '; ')}`);
  }

  return parts.join('\n');
}

/**
 * Build context injection string with summary and TOON-encoded data.
 * Respects token limits by iteratively reducing record count.
 */
export function buildContextInjection(
  data: Record<string, unknown>[],
  options: {
    maxTokens?: number;
    originalCount?: number;
    filterDescription?: string;
  } = {},
): string {
  const { maxTokens = 2000, originalCount, filterDescription } = options;

  const _summary = summarizeDataset(data);
  const reducedData = reduceToTokenLimit(data, maxTokens);
  // Round numeric leaves for LLM-facing display only — the upstream `data`
  // reference (and therefore the staged dataset) is never mutated.
  const displayData = reducedData.map(roundRowForDisplay);
  const encoded = encode(displayData);

  const parts: string[] = [
    // 'DATASET SUMMARY:', summary
  ];

  // Loud banner whenever the rows shown are a subset of the true population —
  // either because the caller filtered before handing us data, or because we
  // had to drop rows to fit the token budget. LLMs have been observed deriving
  // counts, distinct values, and distributions by eyeballing the sample rows,
  // which is always wrong when the sample is smaller than the full dataset.
  const wasTruncated = reducedData.length < data.length;
  const wasFiltered = originalCount !== undefined && originalCount !== data.length;
  if (wasTruncated) {
    parts.push(`SAMPLE (${reducedData.length} of ${data.length} rows, truncated to fit context)`);
    parts.push(SAMPLE_WARNING);
  } else if (wasFiltered) {
    const filterClause = filterDescription ? `, filter: ${filterDescription}` : '';
    parts.push(`SAMPLE (${reducedData.length} of ${originalCount} rows${filterClause})`);
    parts.push(SAMPLE_WARNING);
  }

  parts.push('```');
  parts.push(encoded);
  parts.push('```');

  return parts.join('\n\n');
}
